'use client';

import React from 'react';
import { useLocale } from 'next-intl';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
  Button,
  Input,
  Alert,
  Container,
  Stack,
  Link,
  Logo,
} from '@/components/ui';
import ThemeToggle from '@/components/theme/ThemeToggle';

export interface AuthFormProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  maxWidth?: 'sm' | 'md' | 'lg' | 'full';
  maxHeight?: 'full' | 'auto';
  showLogo?: boolean;
  /** When true, content is rendered without the outer Card wrapper */
  noCard?: boolean;
}

export function AuthForm({
  title,
  subtitle,
  children,
  footer,
  maxWidth = 'sm',
  maxHeight = 'auto',
  showLogo = false,
  noCard = false,
}: AuthFormProps) {
  const content = (
    <>
      <CardHeader className={noCard ? 'p-3 text-center sm:p-4' : 'p-4 text-center sm:p-6'}>
        {showLogo && (
          <div className="mb-4 flex justify-center">
            <Logo size="lg" />
          </div>
        )}
        <CardTitle className="text-2xl">{title}</CardTitle>
        {subtitle && (
          <CardDescription className="text-sm whitespace-pre-line">{subtitle}</CardDescription>
        )}
      </CardHeader>

      <CardContent className={noCard ? 'p-0' : 'p-4 pt-1 sm:p-6 sm:pt-1'}>{children}</CardContent>
      {footer && <CardFooter className="mt-6 flex-col space-y-2">{footer}</CardFooter>}
    </>
  );

  return (
    <div className="items-top flex min-h-screen justify-center sm:items-center sm:p-3">
      {/* Theme toggle in top-right corner */}
      <div className="fixed top-4 right-4 z-50">
        <ThemeToggle collapsed={true} />
      </div>

      <Container
        size={maxWidth}
        className={[maxHeight === 'full' && 'h-full', noCard && '!p-3 sm:!px-4']
          .filter(Boolean)
          .join(' ')}
      >
        {noCard ? (
          <div className="flex w-full flex-col">{content}</div>
        ) : (
          <Card className="w-full">{content}</Card>
        )}
      </Container>
    </div>
  );
}

export interface AuthFormFieldProps {
  label: string;
  type?: 'text' | 'email' | 'password' | 'tel' | 'url';
  name: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  required?: boolean;
  autoComplete?: string;
  placeholder?: string;
  /** id of the element that describes this field (aria-describedby). */
  describedBy?: string;
}

export function AuthFormField({
  label,
  type = 'text',
  name,
  value,
  onChange,
  error,
  required = false,
  autoComplete,
  placeholder,
  describedBy,
}: AuthFormFieldProps) {
  return (
    <Input
      label={label}
      type={type}
      name={name}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      error={error}
      required={required}
      autoComplete={autoComplete}
      placeholder={placeholder}
      aria-describedby={describedBy}
    />
  );
}

export interface AuthFormActionsProps {
  submitText: string;
  loading?: boolean;
  disabled?: boolean;
  secondaryAction?: {
    text: string;
    href: string;
  };
}

export function AuthFormActions({
  submitText,
  loading = false,
  disabled = false,
  secondaryAction,
}: AuthFormActionsProps) {
  const locale = useLocale();

  return (
    <Stack spacing="md">
      <Button type="submit" loading={loading} disabled={disabled} className="w-full">
        {submitText}
      </Button>
      {secondaryAction && (
        <div className="text-center">
          <Link href={`/${locale}${secondaryAction.href}`} color="primary" size="sm">
            {secondaryAction.text}
          </Link>
        </div>
      )}
    </Stack>
  );
}

export interface AuthFormMessageProps {
  type: 'success' | 'error' | 'info';
  message: string;
}

export function AuthFormMessage({ type, message }: AuthFormMessageProps) {
  const variant = type === 'error' ? 'destructive' : type === 'success' ? 'success' : 'default';

  return <Alert variant={variant}>{message}</Alert>;
}
